/**
 * FEAT-044 — cold-start-tolerant react-query retry policy.
 *
 * After idle, Railway/Supabase cold starts take 30–60s. The default retry
 * (2 tries → error) surfaces the red error state during a warmup. Here we treat
 * cold-start signatures — a network error (fetch throws with no HTTP status) or
 * a 502/503/504 — as retryable for longer, with exponential backoff up to a cap.
 * 401/403 are auth, never warmup, so they never retry. Everything else keeps the
 * short default. Pure functions (no React/DOM) so they're unit-testable.
 */

/** Retries for cold-start signatures — with backoff below, ~2 min worst case. */
export const COLD_START_MAX_RETRIES = 8;
/** Retries for other (non-auth, non-cold-start) errors — the prior default. */
export const DEFAULT_MAX_RETRIES = 2;
/** Backoff ceiling per attempt (ms). */
export const RETRY_DELAY_CAP_MS = 30_000;

/** Pull an HTTP status off a thrown error; undefined for network errors. */
export function errorStatus(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : undefined;
}

/** Is this the shape of a backend still coming online (vs. a real failure)? */
export function isColdStartError(error: unknown): boolean {
  const status = errorStatus(error);
  // No status → fetch threw (connection refused / DNS / timeout): backend down/waking.
  if (status === undefined) return true;
  return status === 502 || status === 503 || status === 504;
}

/** react-query `retry` predicate. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = errorStatus(error);
  if (status === 401 || status === 403) return false;
  if (isColdStartError(error)) return failureCount < COLD_START_MAX_RETRIES;
  return failureCount < DEFAULT_MAX_RETRIES;
}

/** react-query `retryDelay` — exponential backoff (1s, 2s, 4s…) capped. */
export function retryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, RETRY_DELAY_CAP_MS);
}
